import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { InfoIcon } from 'lucide-react';

// Import the real API types
import { type CustomAdventure } from '@/utils/businessAppApi';

// Use the actual adventure option type from the API
type AdventureOption = CustomAdventure['options'][0];

interface SimpleAdventureOptionsProps {
  options: AdventureOption[];
  values: Record<string, any>;
  onChange: (optionLabel: string, value: any) => void;
}

export function SimpleAdventureOptions({ options, values, onChange }: SimpleAdventureOptionsProps) {
  // Early return if options are not loaded yet
  if (!options || !Array.isArray(options)) {
    return (
      <div className="text-center py-4 text-gray-500">
        Loading adventure options...
      </div>
    );
  }

  // Filter out "Number of Days" options since days come from the calendar
  const filteredOptions = options.filter(option => {
    const lowerLabel = option.label.toLowerCase();
    const isDaysOption = lowerLabel.includes('day') || 
                        lowerLabel.includes('days') || 
                        lowerLabel.includes('night') || 
                        lowerLabel.includes('nights');
    console.log(`🔍 [SimpleAdventureOptions] Option "${option.label}": isDaysOption=${isDaysOption}`);
    return !isDaysOption;
  });

  console.log(`🔍 [SimpleAdventureOptions] Filtered ${options.length} options to ${filteredOptions.length} (removed ${options.length - filteredOptions.length} days options)`);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>2. Customize Your Adventure</CardTitle>
          <Badge variant="outline" className="text-xs">
            {filteredOptions.length} option{filteredOptions.length !== 1 ? 's' : ''} available
          </Badge>
        </div>
        <p className="text-sm text-gray-600">
          Select your preferences for this adventure
          {filteredOptions.filter(opt => opt.required).length > 0 && (
            <span className="text-red-600 ml-1">
              ({filteredOptions.filter(opt => opt.required).length} required)
            </span>
          )}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {filteredOptions.map((option, index) => (
          <div key={index} className="space-y-2">
            <Label htmlFor={`option-${index}`}>
              {option.label} {option.required && '*'}
            </Label>
            
            {option.type === 'dropdown' && option.options ? (
              <Select 
                value={values[option.label] || ''} 
                onValueChange={(value) => onChange(option.label, value)}
              >
                <SelectTrigger id={`option-${index}`}>
                  <SelectValue placeholder={`Select ${option.label.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {option.options.map((choice, choiceIndex) => {
                    // Handle both string choices and DropdownChoice objects from the API
                    const choiceValue = typeof choice === 'string' ? choice : choice.value;
                    const choiceLabel = typeof choice === 'string' ? choice : choice.value;
                    const hasPricing = typeof choice === 'object' && choice.pricing && choice.pricing.length > 0;
                    
                    return (
                      <SelectItem key={choiceIndex} value={choiceValue}>
                        <div className="flex items-center justify-between w-full">
                          <span>{choiceLabel}</span>
                          {hasPricing && (
                            <Badge variant="secondary" className="ml-2 text-xs">
                              {choice.pricing?.[0]?.type === 'flat' ? `+€${choice.pricing[0].value}` :
                               choice.pricing?.[0]?.type === 'percentage' ? `+${choice.pricing[0].value}%` :
                               choice.pricing?.[0]?.type === 'per_person' ? `+€${choice.pricing[0].value}/person` :
                               choice.pricing?.[0]?.type === 'per_day' ? `+€${choice.pricing[0].value}/day` :
                               'Extra cost'}
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : option.type === 'number' ? (
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const current = Number(values[option.label]) || 0;
                    if (current > 0) onChange(option.label, current - 1);
                  }}
                  className="w-8 h-8 p-0"
                >
                  -
                </Button>
                <Input
                  id={`option-${index}`}
                  type="number"
                  value={values[option.label] || 0}
                  onChange={(e) => onChange(option.label, parseInt(e.target.value) || 0)}
                  className="w-20 text-center"
                  min="0"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const current = Number(values[option.label]) || 0;
                    onChange(option.label, current + 1);
                  }}
                  className="w-8 h-8 p-0"
                >
                  +
                </Button>
              </div>
            ) : option.type === 'checkbox' ? (
              <div className="flex items-center space-x-2">
                <input
                  id={`option-${index}`}
                  type="checkbox"
                  checked={values[option.label] || false}
                  onChange={(e) => onChange(option.label, e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-600">{option.helpText || 'Include this option'}</span>
              </div>
            ) : (
              <Input
                id={`option-${index}`}
                type="text"
                value={values[option.label] || ''}
                onChange={(e) => onChange(option.label, e.target.value)}
                placeholder={`Enter ${option.label.toLowerCase()}`}
              />
            )}
            
            {/* Show pricing information for options with pricing */}
            {option.pricing && option.pricing.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
                <InfoIcon className="h-3 w-3" />
                <span>
                  {option.pricing[0].type === 'flat' ? `Base price: €${option.pricing[0].value}` :
                   option.pricing[0].type === 'percentage' ? `Percentage change: ${option.pricing[0].value}%` :
                   option.pricing[0].type === 'per_person' ? `Cost per person: €${option.pricing[0].value}` :
                   option.pricing[0].type === 'per_day' ? `Cost per day: €${option.pricing[0].value}` :
                   option.pricing[0].type === 'per_person_day' ? `Cost per person per day: €${option.pricing[0].value}` :
                   `Pricing: ${option.pricing[0].description || 'Custom pricing'}`}
                </span>
              </div>
            )}
            
            {/* Show multiplier information if this option is a multiplier */}
            {option.isMultiplier && option.multiplierTarget && (
              <div className="flex items-center gap-2 text-xs text-purple-600 bg-purple-50 p-2 rounded">
                <InfoIcon className="h-3 w-3" />
                <span>This affects pricing for: {option.multiplierTarget}</span>
              </div>
            )}
            
            {/* Show help text if available */}
            {(option as any).helpText && option.type !== 'checkbox' && (
              <p className="text-xs text-gray-500">{(option as any).helpText}</p>
            )}
          </div>
        ))}
        
        {/* Summary of selected options */}
        {Object.keys(values).length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
            <h4 className="font-medium text-sm text-gray-900 mb-2">Selected Options:</h4>
            <div className="space-y-1">
              {Object.entries(values).map(([optionLabel, value]) => {
                if (value === undefined || value === null || value === '' || value === false) return null;
                
                const option = options.find(opt => opt.label === optionLabel);
                const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value.toString();
                
                return (
                  <div key={optionLabel} className="flex justify-between text-xs">
                    <span className="text-gray-600">{optionLabel}:</span>
                    <span className="font-medium text-gray-900">{displayValue}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
